import React, { memo, useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { Editor, JSONContent } from '@tiptap/react';

import { SnapshotVersion, watchPreviewContent } from '@tiptap-pro/extension-snapshot';

import { TiptapCollabProvider } from '@tiptap-pro/provider';

import { Divider } from '@mui/material';

import { VersionsGroup, ButtonGroup, ActionButton } from './styles';
import VersionItem from './VersionItem';
import { getVersionName } from './utils';

interface VersioningContainerProps {
  versions: SnapshotVersion[];
  isOpen: boolean;
  onClose: () => void;
  onRevert: (version: number, versionData: SnapshotVersion) => void;
  provider: TiptapCollabProvider;
  editor: Editor;
}

export const VersioningContainer = memo(
  ({ versions, isOpen, onClose, onRevert, provider, editor }: VersioningContainerProps) => {
    const initialVersion = versions.at(-1)?.version;
    const [currentVersionId, setCurrentVersionId] = useState<number | null>(null);
    const isLatestVersion = versions && versions.length > 0 ? currentVersionId === versions.at(-1)?.version : false;
    const originalContentRef = useRef<JSONContent | null>(null);

    const reversedVersions = useMemo(() => versions.slice().reverse(), [versions]);

    const handleVersionChange = useCallback(
      (newVersion: number) => {
        setCurrentVersionId(newVersion);

        provider.sendStateless(
          JSON.stringify({
            action: 'version.preview',
            version: newVersion,
          })
        );
      },
      [provider]
    );

    const versionData = useMemo(() => {
      if (!versions.length) {
        return null;
      }

      return versions.find((v) => v.version === currentVersionId);
    }, [currentVersionId, versions]);

    useEffect(() => {
      if (isOpen && currentVersionId === null && versions.length > 0 && initialVersion) {
        // Store original content before previewing
        if (editor && !originalContentRef.current) {
          originalContentRef.current = editor.getJSON();
        }

        setCurrentVersionId(initialVersion || 0);

        provider.sendStateless(
          JSON.stringify({
            action: 'version.preview',
            version: initialVersion,
          })
        );
      }
    }, [currentVersionId, versions, isOpen, provider, editor]);

    useEffect(() => {
      if (isOpen) {
        const unbindContentWatcher = watchPreviewContent(provider, (content) => {
          if (editor) {
            editor.commands.setContent(content);
          }
        });

        return () => {
          unbindContentWatcher();
        };
      }
      return undefined;
    }, [isOpen, provider, editor]);

    // Restore original content when version history closes (including unmount from X button)
    useLayoutEffect(() => {
      if (!isOpen) {
        return undefined;
      }

      // Cleanup runs when isOpen changes to false or component unmounts
      return () => {
        // Restore original content if we have it (wasn't already restored by handleClose)
        if (editor && originalContentRef.current) {
          editor.commands.setContent(originalContentRef.current);
          originalContentRef.current = null;
        }
      };
    }, [isOpen, editor]);

    const handleClose = (): void => {
      // Restore original content when closing without reverting
      if (editor && originalContentRef.current) {
        editor.commands.setContent(originalContentRef.current);
        originalContentRef.current = null;
      }
      setCurrentVersionId(null);
      onClose();
    };

    const handleRevert = useCallback(() => {
      // Clear the original content since we're intentionally reverting
      originalContentRef.current = null;
      setCurrentVersionId(null);
      onRevert(currentVersionId || 0, versionData || { version: 0, date: 0, name: '' });
      onClose();
    }, [onRevert, currentVersionId, versionData, onClose]);

    if (!isOpen) {
      return null;
    }

    return (
      <div>
        <Divider sx={{ marginBottom: '12px' }} />
        <VersionsGroup>
          {reversedVersions.map(({ date, version, name }) => (
            <VersionItem
              date={date}
              key={`version_item_${version}`}
              isActive={currentVersionId === version}
              title={name || getVersionName({ version })}
              onClick={() => handleVersionChange(version)}
              isLatestVersion={initialVersion === version}
            />
          ))}
        </VersionsGroup>
        <ButtonGroup>
          <ActionButton type="button" onClick={handleClose}>
            Close
          </ActionButton>
          <ActionButton $isPrimary type="button" disabled={!versionData || isLatestVersion} onClick={handleRevert}>
            Restore
          </ActionButton>
        </ButtonGroup>
      </div>
    );
  }
);

export default VersioningContainer;
